/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoIcon, CameraIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';

// @store
import { useCompanyStore } from '../../../../store/useCompanyStore';
import { useSpinnerStore } from '../../../../store/useSpinnerStore';

// @services
import professionalService from "../../../../services/professional.service";

// @types
import { IDocumentsProfessional, IGalleryCompany } from '../../../../types/professional.types';

// @components
import ItemDocument from './ItemDocument';

// @helpers
import { documentTypes } from '../../../../helpers/functionsUtils';

const DocumentsCompany = () => {
    const { data } = useCompanyStore();
    const { t } = useTranslation();
    const { openSpinner } = useSpinnerStore();
    const [documents, setDocuments] = useState<IDocumentsProfessional[]>([]);
    const [galleryCompany, setGalleryCompany] = useState<{ id: string, value: Blob }[]>([]);
    const [galleryLength, setGalleryLength] = useState<IGalleryCompany[]>([]);
    const currentGalleryRef = useRef<boolean | null>(null);

    const getDocuments = async () => {
        openSpinner(true);
        try {
            const response = await professionalService.getDocumentsByCompany();
            const newResp = response?.filter((item) => item.documentType !== 'CompanyLogo')
            setDocuments(newResp);
            openSpinner(false);
        } catch (error) {
            openSpinner(false);
            toast.error(t("error.callList"));
        }
    }

    const getGallery = async () => {
        currentGalleryRef.current = true;
        if (!data?.professionalId) return;
        openSpinner(true);
        try {
            const response = await professionalService.getGalleryForProfessional(data?.professionalId);
            openSpinner(false);
            if (response?.length) return setGalleryLength(response);
            //if (response?.length) return response?.map((image: IGalleryCompany) => downloadImage(image.professionalImageId));
        } catch (error) {
            toast.error(t("error.callList"))
            openSpinner(false);
        }
    }

    const downloadImage = async (id: string) => {
        openSpinner(true);
        try {
            await professionalService.downloadImageGallery(id).then((response: any) => {
                let list = galleryCompany;
                list.push({ id: id, value: response });
                openSpinner(false);
                return setGalleryCompany(list);
            })
        } catch (error) {
            openSpinner(false);
            toast.error(t("error.callList"));
        }
    }

    useEffect(() => {
        if (galleryLength) {
            galleryLength?.map((image: IGalleryCompany) => downloadImage(image.professionalImageId));
        }
    }, [galleryLength])

    useEffect(() => {
        getDocuments();
        if (!currentGalleryRef.current) getGallery();
    }, [])

    const handleChangeGallery = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const MAX_SIZE = 5 * 1024 * 1024; // 5MB
        const files = Array.from(e.target.files);
        if (files.length > 20) return toast.error(t("professional.profile.maxImage"));
        const imagesList: File[] = files.filter((file) => file.size <= MAX_SIZE);
        const formData = new FormData();

        imagesList.forEach((file) => {
            return formData.append("images", file);
        });
        submitGallery(formData);
    }

    const submitGallery = async (file: FormData) => {
        openSpinner(true);
        setGalleryCompany([]);
        try {
            await professionalService.saveGalleryByCompany(data?.professionalId ?? '', file);
            openSpinner(false);
            getGallery();
            toast.success(t("professional.profile.succesGallery"));
        } catch (error) {
            openSpinner(false);
            toast.error(t("error.postData"));
        }
    }

    const getDocumentFile = (type: string) => {
        const documentFile = documents?.find((item: IDocumentsProfessional) => item?.documentType === type);
        if (documentFile) return documentFile;
        return null;
    }

    const getImage = (id: string) => {
        const image = galleryCompany?.find((item: any) => item?.id === id);
        if (!image) return "";
        return URL.createObjectURL(image.value);
    }

    return (
        <div className="flex flex-col p-6 bg-white dark:bg-dark-cBackground rounded-lg shadow-md h-[85vh] overflow-auto">
            <h3 className="font-semibold mb-3 dark:text-dark-cText">
                {t("professional.profile.documentation")} {data?.name}
            </h3>
            <p className="text-[#787878] dark:text-dark-cText/60 text-sm mb-4">
                {t("professional.profile.remenberText")}
            </p>
            <div className="space-y-3">
                <ItemDocument
                    documentName={t("professional.profile.documentLegalRepresentant")}
                    documentNumber="1"
                    documentFile={getDocumentFile(documentTypes.identityDocument)}
                    getDocuments={getDocuments}
                />

                {!data?.businessInfo?.name?.includes("Persona Natural") &&
                    <ItemDocument
                        documentName={t("professional.profile.certifiedOfChamberAndCommerce")}
                        documentNumber="2"
                        documentFile={getDocumentFile(documentTypes.commerceDocument)}
                        getDocuments={getDocuments}
                    />
                }

                <ItemDocument
                    documentName={t("professional.profile.bankCertificate")}
                    documentNumber="3"
                    documentFile={getDocumentFile(documentTypes.bankCertificate)}
                    getDocuments={getDocuments}
                />

                <ItemDocument
                    documentName={"RUT"}
                    documentNumber="4"
                    documentFile={getDocumentFile(documentTypes.rut)}
                    getDocuments={getDocuments}
                />
            </div>


            <div className="mt-6">
                <h4 className="font-semibold mb-2 dark:text-dark-cText">
                    {t("professional.profile.images")} {data?.name}
                </h4>
                <p className="text-[#787878] dark:text-dark-cText/60 text-sm">
                    {t("professional.profile.textImages")}
                </p>
                <label htmlFor="gallery-company-file">
                    {!galleryCompany.length
                        ? <div className="mt-4 px-4 py-2 bg-[#F8F8F8] dark:bg-gray-800 text-[#A4A4A4] dark:text-dark-cText rounded-lg w-full h-[160px] flex flex-col items-center justify-center gap-[10px] cursor-pointer">
                            <p className='text-[#F0F0F0] text-[20px] m-0'>XERVIX</p>
                            {t("professional.profile.textGallery")}
                            <div className='bg-white flex flex-col justify-center items-center text-[12px] text-[#A4A4A4] p-[5px] border rounded-lg'>
                                <PhotoIcon className='w-7' />
                                {t("professional.profile.gallery")}
                            </div>
                        </div>
                        : <div className='flex flex-col items-center mt-2 text-[#A4A4A4] text-[13px] relative inline-block group'>
                            <CameraIcon className='w-6 text-[#F97068] cursor-pointer' />
                            <div className='absolute left-auto right-auto top-[30px] w-40 bg-gray-300 dark:bg-gray-100 p-2 rounded shadow-lg hidden group-hover:block'>
                                {t("professional.profile.textGallery")}
                            </div>
                        </div>
                    }

                    <input
                        id="gallery-company-file"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleChangeGallery}
                        className="hidden"
                    />
                </label>
            </div>


            {galleryCompany.length
                ? <div className="w-full mt-2 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-[10px]">
                    {
                        galleryCompany?.map((image, index: number) => (
                            <div className='w-full col-spam-1' key={index}>
                                <img
                                    src={getImage(image.id)}
                                    alt="Gallery list"
                                    className='w-full h-[90px] object-cover rounded-xl xl:p-1 '
                                />
                            </div>
                        ))
                    }
                </div>
                : null
            }
        </div>
    )
}

export default DocumentsCompany